// src/components/AddVehicleModal.js
import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, MenuItem, Typography, Checkbox, Select, FormControl, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { addVehicle, updateVehicle, addPost, getVehicleModels } from '../utils/firestoreUtils';
import Compressor from 'compressorjs';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase';
import { AuthContext } from '../utils/AuthContext';

function FieldSection({ label }) {
  return (
    <div className="field-section">
      <div className="field-section-line" />
      <span className="field-section-label">{label}</span>
      <div className="field-section-line" />
    </div>
  );
}

const MOD_CATEGORIES = ['Suspension', 'Electronics', 'Brakes', 'Safety', 'Aerodynamics', 'Wheels/Tires'];

function AddVehicleModal({ open, onClose, userId, vehicle, onDelete }) {
  const { user } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [mods, setMods] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [modCategory, setModCategory] = useState('');
  const [modName, setModName] = useState('');
  const [modDesc, setModDesc] = useState('');
  const [openModModal, setOpenModModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmRemoveMod, setShowConfirmRemoveMod] = useState(false);
  const [modToRemove, setModToRemove] = useState(null);
  const [showDescDialog, setShowDescDialog] = useState(false);
  const [descToShow, setDescToShow] = useState('');
  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [showCategoryError, setShowCategoryError] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [postToCommunity, setPostToCommunity] = useState(false);
  const [communityDescription, setCommunityDescription] = useState('');
  const [modPostToCommunity, setModPostToCommunity] = useState(false);
  const [modCommunityDescription, setModCommunityDescription] = useState('');

  useEffect(() => {
    const loadData = async () => {
      const models = await getVehicleModels();
      setVehicleModels(models);
      if (vehicle) {
        setName(vehicle.name || ''); setModel(vehicle.model || ''); setYear(vehicle.year || '');
        setImages(vehicle.images || []); setMods(vehicle.mods || []); setMainImageIndex(vehicle.mainImageIndex || 0);
      } else {
        setName(''); setModel(''); setYear(''); setImages([]); setMods([]); setMainImageIndex(0);
      }
      setUnsaved(false);
    };
    loadData();
  }, [vehicle]);

  const handleClose = () => {
    if (unsaved && !window.confirm('Unsaved changes. Are you sure?')) return;
    onClose();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (images.length >= 50) { alert('Maximum 50 images.'); return; }
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1600,
      success(result) {
        const storageRef = ref(storage, `users/${userId}/vehicles/${file.name}`);
        uploadBytes(storageRef, result).then(() =>
          getDownloadURL(storageRef).then(url => { setImages(prev => [...prev, url]); setUnsaved(true); })
        );
      },
    });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setMainImageIndex(prev => prev === index ? 0 : prev > index ? prev - 1 : prev);
    setUnsaved(true);
  };

  const addMod = async () => {
    if (!modCategory) { setShowCategoryError(true); return; }
    if (modPostToCommunity && modCommunityDescription.split(' ').length < 10) {
      alert('Community description must be at least 10 words.'); return;
    }
    setMods(prev => [...prev, { category: modCategory, name: modName, desc: modDesc }]);
    if (modPostToCommunity) {
      await addPost({ userId, userName: user?.displayName || 'User', type: 'modification', content: modCommunityDescription, image: images[mainImageIndex] || null, timestamp: new Date(), likes: [], comments: [] });
    }
    setOpenModModal(false); setModCategory(''); setModName(''); setModDesc('');
    setModPostToCommunity(false); setModCommunityDescription(''); setUnsaved(true);
  };

  const removeMod = (index) => { setMods(mods.filter((_, i) => i !== index)); setUnsaved(true); };

  const handleSubmit = async () => {
    if (postToCommunity && communityDescription.split(' ').length < 10) {
      alert('Community description must be at least 10 words.'); return;
    }
    if (vehicle) {
      await updateVehicle(userId, vehicle.id, { name, model, year, images, mods, mainImageIndex });
    } else {
      await addVehicle(userId, { name, model, year, images, mods, mainImageIndex });
      if (postToCommunity) {
        await addPost({ userId, userName: user?.displayName || 'User', type: 'vehicle', content: communityDescription, image: images[mainImageIndex] || null, timestamp: new Date(), likes: [], comments: [] });
      }
    }
    onClose();
  };

  // Group mods by category for display
  const modsByCategory = mods.reduce((acc, mod, idx) => {
    if (!acc[mod.category]) acc[mod.category] = [];
    acc[mod.category].push({ ...mod, index: idx });
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)', m: { xs: 1, sm: 2 } } }}>

      {/* Header */}
      <DialogTitle sx={{ p: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', lineHeight: 1 }}>
              {vehicle ? 'Edit Vehicle' : 'Add Vehicle'}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
              {vehicle ? 'Update your build details' : 'Tell us about your car'}
            </div>
          </div>
          <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: '20px 24px', overflowY: 'auto' }}>

        {/* ── DETAILS ── */}
        <FieldSection label="Vehicle Details" />
        <TextField label="Car Name / Nickname" value={name} onChange={e => { setName(e.target.value); setUnsaved(true); }}
          size="small" fullWidth sx={{ mb: 1.5 }} placeholder="e.g. The Spaniard" />
        <div className="field-row" style={{ marginBottom: 12 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Model</InputLabel>
            <Select value={model} onChange={e => { setModel(e.target.value); setUnsaved(true); }} label="Model">
              {vehicleModels.map(m => <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Year" type="number" value={year} onChange={e => { setYear(e.target.value); setUnsaved(true); }}
            size="small" fullWidth placeholder="e.g. 2024" />
        </div>

        {/* ── IMAGES ── */}
        <FieldSection label="Photos" />
        <label className="file-upload-label" htmlFor="veh-img-upload">
          <span>＋</span> Upload Photo {images.length > 0 ? `(${images.length}/50)` : ''}
        </label>
        <input id="veh-img-upload" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
        {images.length > 0 && (
          <div className="image-strip">
            {images.map((img, idx) => (
              <div key={idx} className="image-thumb">
                <img src={img} alt="" className={mainImageIndex === idx ? 'main-img' : ''} onClick={() => setMainImageIndex(idx)} />
                {mainImageIndex === idx && <div className="image-thumb-badge">M</div>}
                <button className="image-thumb-del" onClick={() => { setImageToDelete(idx); setShowConfirmDeleteImage(true); }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* ── MODIFICATIONS ── */}
        <FieldSection label="Modifications" />
        {Object.entries(modsByCategory).map(([category, catMods]) => (
          <div key={category} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 5 }}>
              {category}
            </div>
            {catMods.map(mod => (
              <div key={mod.index} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 10px', background: 'var(--surface-2)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                marginBottom: 4, fontSize: '0.85rem',
              }}>
                <span style={{ flex: 1 }}>{mod.name}</span>
                {mod.desc && (
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '0.75rem', padding: '2px 6px' }}
                    onClick={() => { setDescToShow(mod.desc); setShowDescDialog(true); }}>
                    info
                  </button>
                )}
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', padding: 2 }}
                  onClick={() => { setModToRemove(mod.index); setShowConfirmRemoveMod(true); }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            ))}
          </div>
        ))}
        <button
          onClick={() => setOpenModModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', background: 'transparent',
            border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)',
            cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)', fontWeight: 500, width: '100%',
            justifyContent: 'center', marginBottom: 4, transition: 'all var(--transition)',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          ＋ Add Modification
        </button>

        {/* Community toggle */}
        <label className="community-toggle">
          <input type="checkbox" checked={postToCommunity} onChange={e => setPostToCommunity(e.target.checked)} />
          <span className="community-toggle-label">Post to Community</span>
        </label>
        {postToCommunity && (
          <TextField label="Share your build story (10+ words)" multiline rows={3}
            value={communityDescription} onChange={e => setCommunityDescription(e.target.value)}
            fullWidth size="small" sx={{ mt: 1 }} />
        )}

        {/* Footer actions */}
        <div className="modal-actions">
          {vehicle ? (
            <button className="admin-btn-deny" style={{ padding: '8px 18px' }} onClick={() => setShowConfirmDelete(true)}>
              Delete Vehicle
            </button>
          ) : <div />}
          <Button onClick={handleSubmit} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
            {vehicle ? 'Save Changes' : 'Add Vehicle'}
          </Button>
        </div>
      </DialogContent>

      {/* ── Add Mod Dialog ── */}
      <Dialog open={openModModal} onClose={() => setOpenModModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 'var(--radius-xl)' } }}>
        <DialogTitle sx={{ p: '16px 20px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', letterSpacing: '0.04em' }}>Add Modification</span>
            <IconButton onClick={() => setOpenModModal(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ p: '16px 20px' }}>
          <TextField select label="Category *" value={modCategory} onChange={e => setModCategory(e.target.value)}
            size="small" fullWidth sx={{ mb: 1.5 }}>
            {MOD_CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="Part Name *" value={modName} onChange={e => setModName(e.target.value)}
            size="small" fullWidth sx={{ mb: 1.5 }} placeholder="e.g. MPP Coilover Conversion" />
          <TextField label="Description (optional)" value={modDesc} onChange={e => setModDesc(e.target.value)}
            multiline rows={2} size="small" fullWidth sx={{ mb: 1 }} />
          <label className="community-toggle" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={modPostToCommunity} onChange={e => setModPostToCommunity(e.target.checked)} />
            <span className="community-toggle-label">Post to Community</span>
          </label>
          {modPostToCommunity && (
            <TextField label="Describe this mod (10+ words)" multiline rows={2}
              value={modCommunityDescription} onChange={e => setModCommunityDescription(e.target.value)}
              fullWidth size="small" sx={{ mt: 1 }} />
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button onClick={addMod} variant="contained" sx={{ textTransform: 'none', fontWeight: 700 }}>Add Mod</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete vehicle */}
      <Dialog open={showConfirmDelete} onClose={() => setShowConfirmDelete(false)}>
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogContent><Typography>Are you sure? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { onDelete(vehicle.id); setShowConfirmDelete(false); onClose(); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm remove mod */}
      <Dialog open={showConfirmRemoveMod} onClose={() => setShowConfirmRemoveMod(false)}>
        <DialogTitle>Remove Modification</DialogTitle>
        <DialogContent><Typography>Remove this mod from your build?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmRemoveMod(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { removeMod(modToRemove); setShowConfirmRemoveMod(false); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Mod description */}
      <Dialog open={showDescDialog} onClose={() => setShowDescDialog(false)}>
        <DialogTitle>Modification Notes</DialogTitle>
        <DialogContent><Typography>{descToShow}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setShowDescDialog(false)} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
      </Dialog>

      {/* Confirm delete image */}
      <Dialog open={showConfirmDeleteImage} onClose={() => setShowConfirmDeleteImage(false)}>
        <DialogTitle>Remove Photo</DialogTitle>
        <DialogContent><Typography>Remove this photo?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDeleteImage(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { removeImage(imageToDelete); setShowConfirmDeleteImage(false); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Category error */}
      <Dialog open={showCategoryError} onClose={() => setShowCategoryError(false)}>
        <DialogTitle>Category Required</DialogTitle>
        <DialogContent><Typography>Please select a category for the modification.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setShowCategoryError(false)} sx={{ textTransform: 'none' }}>OK</Button></DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default AddVehicleModal;
